import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Topbar } from '../components/Topbar';
import { Megaphone, Plus, Trash2, Clock, Search, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, Button, Badge, SearchInput } from '../components/ui';

export function NoticesPage() {
  const { user } = useAuth();
  const { notices, addNotice, removeNotice } = useData();
  const [showAdd, setShowAdd] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterColor, setFilterColor] = useState('all');

  const [newTitle, setNewTitle] = useState('');
  const [newSub, setNewSub] = useState('');
  const [newColor, setNewColor] = useState('purple');
  const [targetCourse, setTargetCourse] = useState('all');

  const { studentEnrollments } = useData();

  const filteredNotices = useMemo(() => {
    return notices
      .filter(n => {
        if (user.type === 'professor') {
          // Professor vê avisos para professores OU avisos que ele mesmo criou para alunos
          return n.type === 'professor' || n.authorId === (user.enrollmentId || user.email);
        }
        if (user.type === 'student') {
             const studentCourses = studentEnrollments.map(e => (e.course || e.courseName || '').toLowerCase());
             if (!n.targetCourse || n.targetCourse === 'all') {
               return n.type === 'student';
             }
             return studentCourses.includes(n.targetCourse.toLowerCase());
           }
        return n.type === user.type;
      })
      .filter(n => 
        n.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        n.sub.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter(n => filterColor === 'all' || n.color === filterColor);
  }, [notices, user.type, searchTerm, filterColor, studentEnrollments]);

  const handleSubmit = (e) => {
    e.preventDefault();
    addNotice({
      title: newTitle,
      sub: newSub,
      type: user.type === 'professor' ? 'student' : user.type, // Professor cria aviso para alunos por padrão
      targetCourse: user.type === 'professor' ? targetCourse : 'all',
      color: newColor,
      icon: 'Megaphone',
      author: user.name,
      authorId: user.enrollmentId || user.email
    });
    setNewTitle('');
    setNewSub('');
    setTargetCourse('all');
    setShowAdd(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Topbar />

      <div className="bg-[#5865F2]/10 border border-[#5865F2]/20 p-6 rounded-[32px] mb-8 flex items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-[#5865F2]/20 rounded-2xl text-[#5865F2]">
            <Megaphone size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#5865F2] mb-1">Central de Comunicação</h3>
            <p className="text-sm text-[#5865F2]/80 leading-relaxed max-w-2xl">
              Todos os horários, avisos, comunicados e atividades acadêmicas são divulgados através do Discord oficial da instituição.
            </p>
          </div>
        </div>
        <Button className="bg-[#5865F2] hover:bg-[#4752C4] border-none whitespace-nowrap">
          Ir para o Discord
        </Button>
      </div>
      
      <Card 
        title="Comunicados e Avisos" 
        subtitle="Fique por dentro das últimas atualizações da universidade."
        actions={
          <div className="flex gap-2">
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
            <select 
              className="manage" 
              value={filterColor} 
              onChange={(e) => setFilterColor(e.target.value)}
              style={{ padding: '0 10px' }}
            >
              <option value="all">Todas Cores</option>
              <option value="purple">Roxo</option>
              <option value="green">Verde</option>
              <option value="orange">Laranja</option>
              <option value="yellow">Amarelo</option>
              <option value="blue">Azul</option>
            </select>
            {user.type === 'professor' && (
              <Button onClick={() => setShowAdd(true)} className="flex items-center gap-2">
                <Plus size={16}/> Criar Aviso
              </Button>
            )}
          </div>
        }
        className="fullPage"
      >
        <div className="noticesList mt-6">
          <AnimatePresence>
            {filteredNotices.length > 0 ? (
              filteredNotices.map((n) => (
                <motion.div 
                  key={n.id} 
                  layout
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="noticeCard"
                >
                  <div className={`noticeIcon ${n.color}`}><Megaphone size={24}/></div>
                  <div className="noticeContent">
                    <div className="noticeHeader">
                      <strong>{n.title}</strong>
                      <div className="noticeMeta">
                        <Clock size={14}/> <span>{n.time}</span>
                      </div>
                    </div>
                    <p>{n.sub}</p>
                  </div>
                  {user.type === 'professor' && (
                    <button className="deleteBtn" onClick={() => removeNotice(n.id)}>
                      <Trash2 size={18}/>
                    </button>
                  )}
                </motion.div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-muted">
                <Megaphone size={48} className="mb-4 opacity-20" />
                <p>Nenhum comunicado encontrado.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </Card>

      {showAdd && (
        <div className="modalOverlay">
          <form className="modal" onSubmit={handleSubmit}>
            <h3>Novo Comunicado</h3>
            <p>O aviso será visível para os alunos de acordo com o curso selecionado.</p>
            
            <div className="formGroup">
              <label>Título do Aviso</label>
              <input 
                required
                placeholder="Ex: Prova Substitutiva"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
              />
            </div>

            <div className="formGroup">
              <label>Público Alvo (Curso)</label>
              <select value={targetCourse} onChange={e => setTargetCourse(e.target.value)}>
                <option value="all">Todos os Alunos</option>
                <option value="Direito">Direito</option>
                <option value="Engenharia Mecânica">Engenharia Mecânica</option>
                <option value="Gastronomia">Gastronomia</option>
                <option value="Medicina">Medicina</option>
              </select>
            </div>

            <div className="formGroup">
              <label>Cor do Ícone</label>
              <select value={newColor} onChange={e => setNewColor(e.target.value)}>
                <option value="purple">Roxo (Padrão)</option>
                <option value="green">Verde (Sucesso)</option>
                <option value="orange">Laranja (Alerta)</option>
                <option value="yellow">Amarelo (Atenção)</option>
                <option value="blue">Azul (Informação)</option>
              </select>
            </div>

            <div className="formGroup">
              <label>Conteúdo</label>
              <textarea 
                required
                placeholder="Descreva os detalhes do comunicado..."
                value={newSub}
                onChange={e => setNewSub(e.target.value)}
                style={{ width: '100%', minHeight: '100px', padding: '12px', background: '#070d19', color: 'white', border: '1px solid var(--line2)', borderRadius: '12px' }}
              />
            </div>

            <div className="modalActions">
              <button type="button" onClick={() => setShowAdd(false)}>Cancelar</button>
              <Button type="submit">Publicar Aviso</Button>
            </div>
          </form>
        </div>
      )}
    </motion.div>
  );
}
